/**
 * DataTableRow Component
 *
 * Renders a single table row with selection, expansion, and actions.
 */

import React, { ReactNode } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ColumnDefinition, ActionDefinition } from './types';
import { staggerDelay } from './stagger';
import { ActionButtons } from './ActionButtons';

interface DataTableRowProps<T> {
  row: T;
  index: number;
  columns: ColumnDefinition<T>[];
  actions?: ActionDefinition<T>[];
  rowId: string;
  selectable?: boolean;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  expandable?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: (id: string) => void;
  renderExpanded?: (row: T) => ReactNode;
  onRowExpand?: (row: T) => void;
  className?: string;
  striped?: boolean;
  hoverable?: boolean;
  onClick?: (row: T) => void;
  backgroundClass?: string;
}

export function DataTableRow<T>({
  row,
  index,
  columns,
  actions,
  rowId,
  selectable = false,
  isSelected = false,
  onSelect,
  expandable = false,
  isExpanded = false,
  onToggleExpand,
  renderExpanded,
  onRowExpand,
  className,
  striped = true,
  hoverable = true,
  onClick,
  backgroundClass
}: DataTableRowProps<T>) {
  const shouldReduceMotion = useReducedMotion();

  const handleToggleExpand = () => {
    if (expandable && onToggleExpand) {
      onToggleExpand(rowId);
      // Trigger lazy load callback on first expand
      if (!isExpanded && onRowExpand) {
        onRowExpand(row);
      }
    }
  };

  const baseRowClass = backgroundClass
    ?? (isSelected
      ? 'bg-purple-50'
      : striped
        ? index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
        : 'bg-white');

  const hoverClass = hoverable
    ? isSelected
      ? 'hover:bg-purple-100'
      : 'hover:bg-gray-100'
    : '';

  const expandedBgClass = isExpanded ? 'bg-purple-50/50' : '';

  const columnCount = columns.length
    + (selectable ? 1 : 0)
    + (expandable ? 1 : 0)
    + (actions && actions.length > 0 ? 1 : 0);

  return (
    <>
      <motion.tr
        initial={shouldReduceMotion ? undefined : { opacity: 0 }}
        animate={shouldReduceMotion ? undefined : { opacity: 1 }}
        transition={{ delay: staggerDelay(index, 0.02, shouldReduceMotion) }}
        className={`
          transition-colors duration-150
          ${baseRowClass}
          ${hoverClass}
          ${expandedBgClass}
          ${className || ''}
          ${onClick ? 'cursor-pointer' : ''}
        `}
        onClick={onClick ? () => onClick(row) : undefined}
      >
        {/* Expand toggle */}
        {expandable && (
          <td className="w-10 px-2 py-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand();
              }}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-purple-600" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </td>
        )}

        {/* Selection checkbox */}
        {selectable && (
          <td className="w-12 px-4 py-4">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelect?.(rowId);
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
          </td>
        )}

        {/* Data columns */}
        {columns.map(column => {
          // Build responsive visibility classes (matching header)
          // hideOnTablet: hidden 640-1023px, show only on lg (≥1024px)
          const responsiveClass = column.hideOnTablet
            ? 'hidden lg:table-cell'  // Only show on desktop (≥1024px)
            : column.hideOnMobile
              ? 'hidden sm:table-cell' // Show on tablet+ (≥640px)
              : '';                     // Always visible

          return (
            <td
              key={column.id}
              className={`
                px-6 py-4 whitespace-nowrap overflow-hidden text-ellipsis
                ${column.cellClassName || ''}
                ${responsiveClass}
              `}
              style={{ textAlign: column.align, width: column.width, minWidth: column.minWidth }}
            >
              {column.accessor(row)}
            </td>
          );
        })}

        {/* Actions column - sticky with fixed width */}
        {actions && actions.length > 0 && (
          <td className={`sticky right-0 z-10 px-4 py-4 whitespace-nowrap text-right ${baseRowClass} border-l border-gray-100 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.05)]`}>
            <ActionButtons row={row} actions={actions} />
          </td>
        )}
      </motion.tr>

      {/* Expanded content */}
      <AnimatePresence>
        {expandable && isExpanded && renderExpanded && (
          <motion.tr
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-gray-50"
          >
            <td colSpan={columnCount} className="px-6 py-4">
              {renderExpanded(row)}
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
}

export default DataTableRow;
